import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Pagination,
  TextField,
  Button,
  IconButton,
  Skeleton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Grid,
  InputAdornment,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tabs,
  Tab
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Add as AddIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { Search, Clear } from '@mui/icons-material';
import Page from 'src/components/Page';
import palette from 'src/theme/palette';
import { getFeaturesCategory, deleteFeaturesCategory, deleteSubCategory } from 'src/ApiServices';
import { toast } from 'react-toastify';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AddEditFeaturesCategoryModal from 'src/sections/dashboard-sections/FeaturesSections/AddEditFeaturesCategoryModal';
import AddEditSubCategoryModal from 'src/sections/dashboard-sections/FeaturesSections/AddEditSubCategoryModal';
import FeaturesViewModal from 'src/sections/dashboard-sections/FeaturesSections/FeaturesViewModal';

export default function FeaturesCategory() {
  const theme = useTheme();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [tempSearch, setTempSearch] = useState('');
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 20,
    total: 0,
    last_page: 1
  });
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [subCategoryModalOpen, setSubCategoryModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ type: 'category', id: null });
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewingCategory, setViewingCategory] = useState(null);

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
    setPage(1);
    setSearch('');
    setTempSearch('');
  };

  const handleSearchSubmit = () => {
    setSearch(tempSearch);
    setPage(1);
  };

  const handleClearSearch = async () => {
    setTempSearch('');
    setSearch('');
    setPage(1);
    await fetchCategoriesData(1);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleAddClick = () => {
    setSelectedCategory(null);
    setModalOpen(true);
  };

  const handleAddSubCategoryClick = () => {
    setSelectedCategory(null);
    setSubCategoryModalOpen(true);
  };

  const handleEditClick = (category) => {
    if (tab === 1) {
      setSelectedCategory({
        parent_id: category.parent_id,
        sub_category: [
          {
            id: category.id,
            name: category.name
          }
        ]
      });
      setSubCategoryModalOpen(true);
    } else {
      setSelectedCategory(category);
      setModalOpen(true);
    }
  };

  const handleViewClick = (category) => {
    // Only show view modal for main category (tab 0)
    if (tab === 0) {
      setViewingCategory(category);
      setViewModalOpen(true);
    }
  };

  const handleCloseViewModal = () => {
    setViewModalOpen(false);
    setViewingCategory(null);
  };

  const handleDeleteMainCategory = (category) => {
    setSelectedCategory(category);
    setDeleteTarget({ type: 'category', id: category.id });
    setDeleteModalOpen(true);
  };

  const handleDeleteSubCategory = (item, subCategoryId = null) => {
    if (subCategoryId) {
      setSelectedCategory(item);
      setDeleteTarget({
        type: 'subcategory',
        id: subCategoryId,
        parentCategoryId: item.id
      });
    } else {
      setSelectedCategory(item);
      setDeleteTarget({
        type: 'subcategory',
        id: item.id,
        parentCategoryId: item.parent_id
      });
    }
    setDeleteModalOpen(true);
  };

  const handleDeleteClick = (item, subCategoryId = null) => {
    if (subCategoryId) {
      handleDeleteSubCategory(item, subCategoryId);
    } else if (tab === 0) {
      handleDeleteMainCategory(item);
    } else {
      handleDeleteSubCategory(item);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSubCategoryModalOpen(false);
    setDeleteModalOpen(false);
    setSelectedCategory(null);
    setDeleteTarget({ type: 'category', id: null });
  };

  const handleModalSuccess = () => {
    handleCloseModal();
    fetchCategoriesData(page);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget.id) return;

    try {
      setDeleteLoading(true);

      if (deleteTarget.type === 'subcategory') {
        await deleteSubCategory(deleteTarget.id);
        toast.success('Subcategory deleted successfully!');
        if (tab === 1) {
          fetchCategoriesData(page);
        }
      } else {
        await deleteFeaturesCategory(deleteTarget.id);
        toast.success('Category deleted successfully!');
      }
      fetchCategoriesData(page);

      setDeleteModalOpen(false);
      setSelectedCategory(null);
      setDeleteTarget({ type: 'category', id: null });
    } catch (error) {
      console.error('Failed to delete:', error);
      toast.error(error.response?.data?.message || `Failed to delete ${deleteTarget.type}!`);
    } finally {
      setDeleteLoading(false);
    }
  };

  const renderSubCategories = (subCategories = [], category) => {
    if (!subCategories || !subCategories.length) return 'N/A';

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {subCategories.map((subCat) => (
          <Chip
            key={subCat.id}
            label={subCat.name}
            variant="outlined"
            size="small"
            // onClick={() => handleViewSubCategoryClick({ ...subCat, parent_name: category.name })}
            onDelete={(e) => {
              e.stopPropagation();
              handleDeleteClick(category, subCat.id);
            }}
            deleteIcon={
              <Tooltip title="Delete Subcategory">
                <CancelIcon fontSize="small" />
              </Tooltip>
            }
            sx={{
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: palette.action.hover
              },
              borderColor: palette.grey[400],
              color: palette.text.primary
            }}
          />
        ))}
      </Box>
    );
  };
  const handleViewSubCategoryClick = (subCategory) => {
    setViewingCategory(subCategory);
    setViewModalOpen(true);
  };

  const renderCategoryChips = (categoryData = [], parentCategory) => {
    if (!categoryData.length) return 'N/A';

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {categoryData.map((cat) => (
          <Chip
            key={cat.id}
            label={cat.name}
            variant="outlined"
            size="small"
            onClick={() => console.log('Category chip clicked:', cat)}
            sx={{
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: palette.action.hover
              },
              borderColor: palette.grey[400],
              color: palette.text.primary
            }}
          />
        ))}
      </Box>
    );
  };

  const fetchCategoriesData = async (currentPage) => {
    setLoading(true);
    try {
      const params = { page: currentPage, limit: 10 };
      if (search) params.search = search;

      const response = await getFeaturesCategory(params);
      const allCategories = response.data || [];
      const allMainCategories = allCategories.filter((c) => !c.parent_id);

      let filtered = [];
      let allSubCategories = [];

      allCategories.forEach((category) => {
        if (category.sub_category && category.sub_category.length > 0) {
          const subCategoriesWithParent = category.sub_category.map((subCat) => ({
            ...subCat,
            parent_id: category.id,
            parent_name: category.name,
            parent_category: category
          }));
          allSubCategories = [...allSubCategories, ...subCategoriesWithParent];
        }
      });

      if (tab === 0) {
        filtered = allMainCategories;
      } else {
        filtered = allSubCategories;
      }

      setCategories(filtered);

      if (response.pagination) {
        const totalItems = tab === 0 ? allMainCategories.length : allSubCategories.length;
        setPagination({
          ...response.pagination,
          total: totalItems,
          last_page: Math.ceil(totalItems / 10) || 1
        });
      } else {
        setPagination({
          current_page: currentPage,
          per_page: 10,
          total: filtered.length,
          last_page: Math.ceil(filtered.length / 10) || 1
        });
      }
    } catch (error) {
      console.error('Failed to load categories');
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategoriesData(page);
  }, [page, search, tab]);

  const getParentCategoryName = (parentId) => {
    const parentCategory = categories.find((cat) => cat.id === parentId);
    return parentCategory ? parentCategory.name : 'N/A';
  };

  const stickyColumnStyle = (isLeft = true) => ({
    px: 2,
    position: { xs: 'static', md: 'sticky' },
    [isLeft ? 'left' : 'right']: { xs: 'auto', md: 0 },
    zIndex: 1,
    backgroundColor: theme.palette.background.paper,
    minWidth: isLeft ? 200 : 120,
    textAlign: isLeft ? 'left' : 'center'
  });

  const renderSkeletonRows = () => {
    return Array.from({ length: 5 }).map((_, index) => (
      <TableRow key={index}>
        <TableCell sx={stickyColumnStyle(true)}>
          <Skeleton variant="text" />
        </TableCell>
        {tab === 1 && (
          <TableCell sx={{ px: 2 }}>
            <Skeleton variant="text" />
          </TableCell>
        )}
        <TableCell sx={{ px: 2 }}>
          <Skeleton variant="text" width={150} />
        </TableCell>
        <TableCell sx={stickyColumnStyle(false)}>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
            <Skeleton variant="circular" width={32} height={32} />
            <Skeleton variant="circular" width={32} height={32} />
          </Box>
        </TableCell>
      </TableRow>
    ));
  };

  const renderTableHeaders = () => {
    if (tab === 0) {
      return (
        <>
          <TableCell sx={{ ...stickyColumnStyle(true), zIndex: 2 }}>Category Name</TableCell>
          <TableCell sx={{ px: 2 }}>Sub Categories</TableCell>
          <TableCell sx={{ ...stickyColumnStyle(false), zIndex: 2 }}>Actions</TableCell>
        </>
      );
    } else {
      return (
        <>
          <TableCell sx={{ ...stickyColumnStyle(true), zIndex: 2 }}>Sub Category Name</TableCell>
          <TableCell sx={{ px: 2 }}>Parent Category</TableCell>
          <TableCell sx={{ ...stickyColumnStyle(false), zIndex: 2 }}>Actions</TableCell>
        </>
      );
    }
  };

  const renderTableRows = () => {
    if (categories.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
            <Typography color="textSecondary">
              No {tab === 0 ? 'categories' : 'sub categories'} found
            </Typography>
          </TableCell>
        </TableRow>
      );
    }

    return categories.map((category) => (
      <TableRow
        key={category.id}
        sx={{
          '&:hover': { backgroundColor: palette.action.hover },
          transition: 'background-color 0.2s ease'
        }}
      >
        <TableCell sx={{ px: 2 }}>
          <Typography variant="body2">{category?.name || 'N/A'}</Typography>
        </TableCell>

        {tab === 0 ? (
          <TableCell sx={{ px: 2 }}>
            {renderSubCategories(category?.sub_category, category)}
          </TableCell>
        ) : (
          <TableCell sx={{ px: 2 }}>
            <Typography variant="body2">
              {category?.parent_name || getParentCategoryName(category?.parent_id)}
            </Typography>
          </TableCell>
        )}

        <TableCell sx={stickyColumnStyle(false)}>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
            {/* View Button - Only for main category tab */}
            {tab === 0 && (
              <Tooltip title="View">
                <IconButton
                  size="small"
                  onClick={() => handleViewClick(category)}
                  sx={{ color: palette.info.main }}
                >
                  <ViewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            {/* Edit Button */}
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={() => handleEditClick(category)}
                sx={{ color: palette.primary.main }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {/* Delete Button */}
            <Tooltip title="Delete">
              <IconButton
                size="small"
                onClick={() => {
                  if (tab === 0) {
                    handleDeleteClick(category);
                  } else {
                    handleDeleteClick(category);
                  }
                }}
                sx={{ color: palette.error.main }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </TableCell>
      </TableRow>
    ));
  };
  const handleViewModalDelete = async (category, subCategoryId) => {
    try {
      await deleteSubCategory(subCategoryId);
      toast.success('Subcategory deleted successfully!');
      fetchCategoriesData(page);
    } catch (error) {
      console.error('Failed to delete subcategory:', error);
      toast.error(error.response?.data?.message || 'Failed to delete subcategory!');
    }
  };

  const getDeleteMessage = () => {
    if (deleteTarget.type === 'subcategory') {
      const subCategoryName =
        selectedCategory?.sub_category?.find((sub) => sub.id === deleteTarget.id)?.name ||
        'this subcategory';

      return `Are you sure you want to delete subcategory <strong>"${subCategoryName}"</strong>?`;
    }

    return `Are you sure you want to delete ${tab === 0 ? 'category' : 'sub category'} <strong>"${
      selectedCategory?.name
    }"</strong>?`;
  };

  return (
    <Page title={`Features Category | ${process.env.REACT_APP_PROJECTNAME}`}>
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 2,
            mb: 3,
            pb: 2,
            pl: 1
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: 20, md: 25 },
              fontFamily: 'poppinsSemiBold',
              color: palette.primary.titledark
            }}
          >
            Features Category
          </Typography>
        </Box>

        <Box
          sx={{
            mb: 3,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            gap: { xs: 2, md: 0 }
          }}
        >
          {/* Tabs */}
          <Tabs value={tab} onChange={handleTabChange} sx={{ mb: 2 }}>
            <Tab label="Main Category" />
            <Tab label="Sub Category" />
          </Tabs>

          {/* Add Button per tab */}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setSelectedCategory(null);
              if (tab === 0) {
                setModalOpen(true);
              } else {
                setSubCategoryModalOpen(true);
              }
            }}
            sx={{
              background: palette.primary.main,
              color: palette.primary.contrastText,
              '&:hover': { background: palette.primary.dark }
            }}
          >
            {tab === 0 ? 'Add Feature Category' : 'Add Sub Category'}
          </Button>
        </Box>

        <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <Grid item xs={12} md={10}>
            <TextField
              variant="outlined"
              placeholder={`Search ${tab === 0 ? 'categories' : 'sub categories'}...`}
              size="medium"
              fullWidth
              value={tempSearch}
              onChange={(e) => setTempSearch(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearchSubmit()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: palette.text.secondary }} />
                  </InputAdornment>
                ),
                endAdornment: tempSearch && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleClearSearch} edge="end">
                      <Clear fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{
                backgroundColor: theme.palette.background.paper,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: theme.palette.divider },
                  '&:hover fieldset': { borderColor: theme.palette.primary.main },
                  '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main }
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="contained"
              onClick={handleSearchSubmit}
              fullWidth
              sx={{
                height: 56,
                fontSize: '14px',
                fontFamily: 'poppinsMedium'
              }}
              startIcon={<SearchIcon />}
            >
              Search
            </Button>
          </Grid>
        </Grid>

        <Card>
          <CardContent sx={{ p: 0 }}>
            <TableContainer
              sx={{
                width: '100%',
                maxWidth: '100%',
                overflowX: 'auto',
                WebkitOverflowScrolling: 'touch',
                '&::-webkit-scrollbar': {
                  height: 6
                },
                '&::-webkit-scrollbar-track': {
                  background: palette.grey[200]
                },
                '&::-webkit-scrollbar-thumb': {
                  background: palette.grey[400],
                  borderRadius: 4
                }
              }}
            >
              <Table
                sx={{
                  minWidth: 600,
                  '& .MuiTableCell-head': {
                    backgroundColor: theme.palette.background.paper,
                    color: palette.primary.tableHeadTitle,
                    fontFamily: 'poppinsSemiBold',
                    fontSize: '14px',
                    borderBottom: `2px solid ${palette.divider}`,
                    py: 2
                  },
                  '& .MuiTableCell-body': {
                    color: palette.text.primary,
                    fontFamily: 'poppinsRegular',
                    fontSize: '14px',
                    borderBottom: `1px solid ${palette.divider}`,
                    py: 2
                  }
                }}
              >
                <TableHead>
                  <TableRow>{renderTableHeaders()}</TableRow>
                </TableHead>
                <TableBody>{loading ? renderSkeletonRows() : renderTableRows()}</TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {!loading && pagination.total >= 0 && pagination.last_page >= 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'end', mt: 3 }}>
            <Pagination
              count={pagination.last_page}
              page={pagination.current_page}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        )}

        {/* Add/Edit Feature Category Modal */}
        <AddEditFeaturesCategoryModal
          open={modalOpen}
          onClose={handleCloseModal}
          onSuccess={handleModalSuccess}
          category={selectedCategory}
        />
        <FeaturesViewModal
          open={viewModalOpen}
          onClose={handleCloseViewModal}
          category={viewingCategory}
          onDeleteSubCategory={(subCategoryId) => {
            setSelectedCategory(viewingCategory);
            setDeleteTarget({
              type: 'subcategory',
              id: subCategoryId,
              parentCategoryId: viewingCategory.id
            });
            setDeleteModalOpen(true);
            setViewModalOpen(false);
          }}
        />

        {/* Add/Edit Sub Category Modal */}
        <AddEditSubCategoryModal
          open={subCategoryModalOpen}
          onClose={handleCloseModal}
          onSuccess={handleModalSuccess}
          category={selectedCategory}
          isMainCategoryTab={tab === 0}
        />

        {/* Delete Confirmation Modal */}
        <Dialog
          open={deleteModalOpen}
          onClose={handleCloseModal}
          PaperProps={{
            sx: {
              borderRadius: 2,
              padding: 1,
              margin: { xs: 2, sm: 3 },
              maxWidth: '400px',
              width: '100%'
            }
          }}
        >
          <DialogTitle sx={{ textAlign: 'center', py: 3, pb: 2 }}>
            <WarningAmberIcon
              sx={{
                fontSize: 60,
                color: palette.warning.main,
                mb: 2
              }}
            />
            <Typography
              variant="h6"
              sx={{ fontFamily: 'poppinsSemiBold', color: palette.text.primary }}
            >
              Confirm Deletion
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ textAlign: 'center', pb: 2 }}>
            <DialogContentText
              sx={{ color: palette.text.secondary, fontFamily: 'poppinsRegular' }}
              dangerouslySetInnerHTML={{ __html: getDeleteMessage() }}
            />
            <br />
            This action cannot be undone.
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 3, px: 3 }}>
            <Button
              onClick={handleCloseModal}
              variant="outlined"
              sx={{
                borderColor: palette.grey[400],
                color: palette.text.primary,
                minWidth: 100
              }}
              disabled={deleteLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              variant="contained"
              sx={{
                backgroundColor: palette.error.main,
                color: palette.error.contrastText,
                minWidth: 100,
                '&:hover': {
                  backgroundColor: palette.error.dark
                }
              }}
              disabled={deleteLoading}
            >
              {deleteLoading ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Page>
  );
}
